import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { CategoryItem } from '../types/category';
import { MenuItem } from '../types/menu';
import { CinemaProvider, Movie } from '../types/movie';

export interface GenreInterface {
  name: string;
  slug: string;
}

export type Genre = string & GenreInterface;

export interface GlobalMoviesInterface {
  movies: Movie[];
  sessions: any[];
}

export interface GlobalMenusInterface {
  header?: MenuItem[];
  footer?: MenuItem[];
}

export interface DefaultPagesInterface {
  pagesDisclaimer?: string;
  pagesHome?: string;
  pagesPrivacyPolicy?: string;
  pagesTermsAndConditions?: string;
  pagesMallMap?: string;
}

export interface GlobalMapsMIKeyInterface {
  id: string;
  key: string;
}

export interface GlobalMapsSearchKeyInterface {
  id: string;
  secret: string;
}

export interface GlobalMapsConfigInterface {
  language: string;
  miKey: GlobalMapsMIKeyInterface;
  searchKey: GlobalMapsSearchKeyInterface;
  id: string;
  secret: string;
  venue: string;
  vertical: string;
}

export interface GlobalGeneralInterface {
  googleTagManagerTag: string;
  infoDeskHours: string;
  mallAddress: string;
  mallIcon: string;
  mallLogo: string;
  managementOfficeHours: string;
  mapsConfig: GlobalMapsConfigInterface;
  mapsEnabled: boolean;
  metaKeywords: string;
  name: string;
  openingTime: string;
  seoDescription: string;
  seoTitle: string;
  timeZone: string;
  websiteURL: string;
}

export interface GlobalSocialInterface {
  facebookAppId: string;
  facebookPageURL: string;
  instagramAccountURL: string;
  linkedInProfileURL: string;
  openGraphDescription: string;
  openGraphImage: string;
  openGraphTitle: string;
  pinterestURL: string;
  twitterPageURL: string;
  whatsappBusinessAccount: string;
  youtubeChannelURL: string;
}

export interface GlobalDataInterface {
  menus: GlobalMenusInterface;
  movies: GlobalMoviesInterface;
  comingSoon: Movie[];
  nowShowing: Movie[];
  comingSoonGenres: CategoryItem[];
  nowShowingGenres: CategoryItem[];
  cinemaProvider: CinemaProvider;
  defaultPages: DefaultPagesInterface;
  social: GlobalSocialInterface;
  general: GlobalGeneralInterface;
}

/**
 * Global data service used to store Mall, Menu & Movie data
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalDataService {
  globalData: GlobalDataInterface;
  dataChange: Subject<any> = new Subject<any>();

  constructor(private datePipe: DatePipe) {
    this.dataChange.subscribe((value) => {
      this.globalData = value;
    });
  }

  /**
   * Set mall data
   */
  setMallData(data) {
    this.globalData = {
      ...this.globalData,
      ...data,
    };
    this.dataChange.next(this.globalData);
  }

  /**
   * Set Menu data
   */
  setMenuData(type, data) {
    this.globalData = {
      ...this.globalData,
      menus: {
        ...this.globalData?.menus,
        [type]: data,
      },
    };
    this.dataChange.next(this.globalData);
  }

  /**
   * Return global data
   */
  getGlobalData() {
    return this.globalData;
  }

  /**
   * Set movie data
   */
  setMovieData(movies) {
    this.globalData = {
      ...this.globalData,
      movies: movies,
    };

    if (this.globalData.cinemaProvider.slug === 'numetro') {
      const relevantSessions = [];
      movies.sessions.forEach((session) => {
        if (
          session.cineplex_id ===
          this.globalData.cinemaProvider.cineplexId.toString()
        ) {
          relevantSessions.push(session);
        }
      });

      this.globalData.movies.sessions = relevantSessions;

      let movieSessions;
      this.globalData.nowShowing = movies.movies
        .filter((movie) => {
          movieSessions = relevantSessions.filter((session) => {
            return session.movie_id == movie.id;
          });

          return movieSessions.length > 0;
        })
        .map((movie) => {
          return {
            ...movie,
            is_2d:
              relevantSessions.filter(
                (session) =>
                  session.is_2d === '1' && movie.id === session.movie_id
              ).length > 0
                ? '1'
                : '0',
            is_3d:
              relevantSessions.filter(
                (session) =>
                  session.is_3d === '1' && movie.id === session.movie_id
              ).length > 0
                ? '1'
                : '0',
            is_4dx:
              relevantSessions.filter(
                (session) =>
                  session.is_4dx === '1' && movie.id === session.movie_id
              ).length > 0
                ? '1'
                : '0',
            is_vip:
              relevantSessions.filter(
                (session) =>
                  session.is_vip === '1' && movie.id === session.movie_id
              ).length > 0
                ? '1'
                : '0',
            is_xtreme:
              relevantSessions.filter(
                (session) =>
                  session.is_xtreme === '1' && movie.id === session.movie_id
              ).length > 0
                ? '1'
                : '0',
          };
        })
        .sort((a, b) => {
          if (a.is_xtreme !== b.is_xtreme) {
            return b.is_xtreme - a.is_xtreme;
          }
          if (a.is_vip !== b.is_vip) {
            return b.is_vip - a.is_vip;
          }
          if (a.is_4dx !== b.is_4dx) {
            return b.is_4dx - a.is_4dx;
          }
          if (a.is_3d !== b.is_3d) {
            return b.is_3d - a.is_3d;
          }
          if (a.is_2d !== b.is_2d) {
            return b.is_2d - a.is_2d;
          }
          return 0;
        });

      this.globalData.comingSoon = movies.movies.filter((movie) => {
        return (
          movie.release_date > this.datePipe.transform(new Date(), 'yyyy-MM-dd')
        );
      });

      this.globalData.nowShowing.forEach((movie) => {
        movie.genres?.forEach((genre: Genre) => {
          if (!this.globalData.nowShowingGenres)
            this.globalData.nowShowingGenres = [];

          if (!this.globalData.nowShowingGenres.some((e) => e.title === genre))
            this.globalData.nowShowingGenres.push({
              title: genre,
              slug: genre.replace('-', '').toLowerCase(),
            });
        });
      });

      this.globalData.comingSoon.forEach((movie) => {
        movie.genres?.forEach((genre: Genre) => {
          if (!this.globalData.comingSoonGenres)
            this.globalData.comingSoonGenres = [];

          if (!this.globalData.comingSoonGenres.some((e) => e.title === genre))
            this.globalData.comingSoonGenres.push({
              title: genre,
              slug: genre.replace('-', '').toLowerCase(),
            });
        });
      });
    } else {
      let activeMovies: string[] = [];

      Object.keys(movies.sessions).map((key, index) => {
        Object.keys(movies.sessions[key]).map((key2, index2) => {
          if (key2 == this.globalData.cinemaProvider.cineplexId.toString())
            activeMovies.push(key);
        });
      });

      this.globalData.nowShowing = movies.movies
        .filter((movie) => {
          return (
            movie.now_showing === true &&
            movie.coming_soon === false &&
            activeMovies.includes(movie.id)
          );
        })
        .sort((a, b) => {
          if (a.experiences.includes('Nouveu') !== b.experiences.includes('Nouveu')) {
            return Number(b.experiences.includes('Nouveu')) - Number(a.experiences.includes('Nouveu'));
          }
          if (a.experiences.includes('3D IMAX') !== b.experiences.includes('3D IMAX')) {
            return Number(b.experiences.includes('3D IMAX')) - Number(a.experiences.includes('3D IMAX'));
          }
          if (a.experiences.includes('3D') !== b.experiences.includes('3D')) {
            return Number(b.experiences.includes('3D')) - Number(a.experiences.includes('3D'));
          }
          if (a.experiences.includes('2D IMAX') !== b.experiences.includes('2D IMAX')) {
            return Number(b.experiences.includes('2D IMAX')) - Number(a.experiences.includes('2D IMAX'));
          }
          if (a.experiences.includes('2D') !== b.experiences.includes('2D')) {
            return Number(b.experiences.includes('2D')) - Number(a.experiences.includes('2D'));
          }
          return 0;
        });

      this.globalData.comingSoon = movies.movies.filter((movie) => {
        return (
          movie.coming_soon === true &&
          movie.now_showing === false &&
          movie.opening_date_string !== undefined
        );
      });

      this.globalData.nowShowing.forEach((movie) => {
        movie.genres?.forEach((genre: Genre) => {
          if (!this.globalData.nowShowingGenres)
            this.globalData.nowShowingGenres = [];

          if (
            !this.globalData.nowShowingGenres.some(
              (e) => e.title === genre.name
            )
          )
            this.globalData.nowShowingGenres.push({
              title: genre.name,
              slug: genre.name.replace('-', '').toLowerCase(),
            });
        });
      });

      this.globalData.comingSoon.forEach((movie) => {
        movie.genres?.forEach((genre: Genre) => {
          if (!this.globalData.comingSoonGenres)
            this.globalData.comingSoonGenres = [];

          if (
            !this.globalData.comingSoonGenres.some(
              (e) => e.title === genre.name
            )
          )
            this.globalData.comingSoonGenres.push({
              title: genre.name,
              slug: genre.name.replace('-', '').toLowerCase(),
            });
        });
      });
    }

    this.dataChange.next(this.globalData);
  }

  /**
   * Return movie data
   */
  getMovieData() {
    return this.globalData.movies;
  }
}
